import { ILogger, ITopicEnvelope } from '@sparkware/uc-sdk-core';
import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import { WindowToken } from '../../injection-tokens';
import { Utils } from '../utils/utils';
import ServiceProductPackageApi, {
    IOpenServiceProductPackageDataRequest,
} from '../../../APIs/ServiceProductPackageApi';
import { ClientTracking } from '../tracking/models/clientTracking';
import { GNWPElasticEvents } from './enums';
import { ViewInjector } from '../view-injector';
import { CloseSessionReason, IdsOfElements } from '../../models/enums/general-enums';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ICRM } from '../crm/models';
import { IOpenServiceAuthenticatedParams } from './models';
import { GNWPUtils } from './gnwp-utils';
import { UrlUtils } from '../utils/urlUtils';
import { SessionSimpleStoreService } from '../storage/implementations/simple-store';
import { ILogout } from '../logout/models';

@Service()
export class ServiceProduct {
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private readonly _utils: Utils;
    private readonly _clientTracking: ClientTracking;
    private readonly _clientTrackingEnvelop: ITopicEnvelope;
    private readonly _viewInjector: ViewInjector;
    private readonly _gnwpUtils: GNWPUtils;
    private readonly _urlUtls: UrlUtils;
    private readonly _sessionSimpleStoreService: SessionSimpleStoreService;

    private get _crmPromise(): Promise<ICRM> {
        return LoaderManager.Instance.CRMLoader.Instance;
    }

    private get _logoutPromise(): Promise<ILogout> {
        return LoaderManager.Instance.LogoutLoader.Instance;
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('serviceProduct');
        this._window = Container.get(WindowToken);
        this._utils = Container.get(Utils);
        this._clientTracking = Container.get(ClientTracking);
        this._viewInjector = Container.get(ViewInjector);
        this._gnwpUtils = Container.get(GNWPUtils);
        this._urlUtls = Container.get(UrlUtils);
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);

        this._clientTrackingEnvelop = {
            headers: {
                publisher: 'serviceProduct',
            },
            topic: null,
        };
    }

    public async openServiceAuthenticated(params: IOpenServiceAuthenticatedParams): Promise<void> {
        const name = 'open-service-authenticated';
        const key = this._utils.createDebounceKey(name, Object.entries(params));

        return this._utils.debounceExecution(name, key, async () => {
            const data: IOpenServiceProductPackageDataRequest = {
                SubBrandId: params.subBrandId,
                ProductPackageId: params.productPackageId,
                AdditionalActivationParameters: params.additionalActivationParameters,
            };

            this._clientTracking.onSendEvent(
                {
                    event: GNWPElasticEvents.OpenServiceAuthenticated,
                    dataString: JSON.stringify(params),
                },
                this._clientTrackingEnvelop,
            );

            const apiResult =
                await ServiceProductPackageApi.GetServiceProductPackageDataAuthenticated(data);
            await this._openServiceAuthenticatedHandler(params, apiResult.response);
        });
    }

    private async _openServiceAuthenticatedHandler(
        params: IOpenServiceAuthenticatedParams,
        apiResult: any,
    ): Promise<void> {
        const {
            productPackageId,
            width,
            height,
            onClose,
            linkId,
            target,
            openInMobileContainer,
            showCloseButton,
            paramsToAdd,
            getUrl,
            functionToInvoke,
        } = params;

        if (apiResult.isOk) {
            const deviceData = this._window.pageContextManager.getDeviceData();
            let paramsRecord: Record<string, string> = {
                UserInfo: String(apiResult.UserInfo),
                BrandId: String(apiResult.BrandId),
                SourceSubBrand: String(apiResult.SourceSubBrandID),
                SourceProductPackage: String(deviceData.productPackage),
                TargetProductPackage: String(apiResult.TargetProductPackage),
                IsRealMode: String(apiResult.IsRealMode),
                SpecificNavigation: decodeURIComponent(apiResult.SpecificNavigation),
                ClientVersion: String(deviceData.clientVersion),
                EncryptionType: String(apiResult.EncryptionType),
                PasswordEncryptionType: String(apiResult.PasswordEncryptionType),
                LangID:
                    (productPackageId === 11 || productPackageId === 69) &&
                    apiResult.LangID &&
                    apiResult.LangID.toLowerCase() === 'rum'
                        ? 'ron'
                        : String(apiResult.LangID),
                CurrencyCode: String(apiResult.CurrencyCode),
                GoogleSessionID: String(this._utils.GetGALinkerParam()),
            };
            if (apiResult.TargetSubBrand != null) {
                paramsRecord['TargetSubBrand'] = String(apiResult.TargetSubBrand);
            }
            if (linkId != null) {
                paramsRecord['LinkID'] = String(linkId);
            }
            if (paramsToAdd != null) {
                for (const [key, value] of Object.entries(paramsToAdd)) {
                    paramsRecord[key] = value;
                }
            }

            let formTarget = '_blank';

            let effectiveShowCloseButton = showCloseButton;
            if (typeof effectiveShowCloseButton === 'undefined') effectiveShowCloseButton = true;

            if (width != null && height != null) {
                if (effectiveShowCloseButton) {
                    this._viewInjector.InjectWrappingDivWithCloseButton(
                        null,
                        width,
                        height,
                        true,
                        onClose,
                        productPackageId,
                    );
                } else {
                    let addBorder = false;
                    const isMobile = deviceData.isMobile;
                    if (isMobile && this._sessionSimpleStoreService.get('isOmgMsg')) {
                        addBorder = true;
                    }
                    this._viewInjector.InjectWrappingDiv(
                        null,
                        width,
                        height,
                        addBorder,
                        this._sessionSimpleStoreService.get('isOmgMsg') === 'true',
                    );
                }
                let iFrameName = IdsOfElements.GeneralIframe;
                const wrapDiv = this._window.document.getElementById(IdsOfElements.WrapDiv);
                if (
                    wrapDiv != null &&
                    this._sessionSimpleStoreService.get('isOmgMsg') != null &&
                    productPackageId === 76
                ) {
                    iFrameName = IdsOfElements.OmgGeneralIframe;
                }
                formTarget = iFrameName;
            }

            if (openInMobileContainer) {
                this._viewInjector.OpenMobileContainerDiv(null, effectiveShowCloseButton);
                formTarget = IdsOfElements.GeneralIframe;
            }

            if (target) {
                if (target === 'native') {
                    let url = apiResult.GnwpUrl + '?NativeMode=true';
                    for (const [key, value] of Object.entries(paramsRecord)) {
                        url += '&' + key + '=' + encodeURIComponent(value);
                    }
                    return (await this._crmPromise)?.onExternalLinkOpen({ Url: url });
                } else {
                    formTarget = target;
                }
            }

            if (getUrl) {
                const url = apiResult.GnwpUrl + '?' + this._urlUtls.recordToQueryStr(paramsRecord);
                if (functionToInvoke !== undefined) {
                    functionToInvoke(url);
                }
            } else {
                this._gnwpUtils.addFormToBody(apiResult.GnwpUrl, paramsRecord, 'post', formTarget);
            }
        } else {
            if (apiResult.ErrorCode === 5 || apiResult.ErrorCode === 6) {
                this._logger.debug('GetToken failed, performing logout');
                await (await this._logoutPromise)?.doLogout(CloseSessionReason.ClientDisconnected);
            }
            this._logger.debug('Error in call to OpenServiceProductPackage');
        }
    }
}
